import { Button, Checkbox, Flex, Image, Input, InputNumber, message, Modal, ModalProps, Select, Switch, Tabs, Tooltip, Typography } from "antd";
import { DataMapping, DataMappingUpload, useDeleteDataMappingMutation, useGetFormsQuery, usePostDataMappingMutation, usePutDataMappingMutation } from "../../../api/dataMapping/dataMappingApi";
import { useState } from "react";
import CountryEditor from "./CountryEditor";
import UploadButton from "../../components/UploadButton";

const { Text } = Typography;

const productTypeOptions = [
  { label: "eSIM", value: "esim" },
  { label: "Physical SIM", value: "psim" },
]

const webShopTypeOptions = [
  { label: "SIMPLY Product", value: "simply" },
  { label: "Local Product", value: "local" }
];

// To be transferred to DDB
const vendorIds = [
  { label: "Joytel", value: "joytel" },
  { label: "Slowboat", value: "slowboat" },
  { label: "TGT", value: "tgt" },
]

const newMappingTemplate: DataMappingUpload = {
  InternalSku: "",
  FormId: "",
  Type: "",
  VendorId: "",
  VendorSku: "",
  Countries: [],
  ItemDetails: {
    Title: "",
    ImageUrl: "",
    Price: "0.00",
    Enabled: false,
  },
  RedemptionDetails: {
    ImageUrl: "",
    Description: "",
    ApnSetting: ""
  },
  DistributorCode: "",
  TakeappVariantId: "",
}

interface ManageMappingModalProps extends ModalProps {
  m: DataMapping | null;  // undefined means create new mapping
  onOk: () => void;
  onCancel: () => void;
}

export default function ManageMappingModal(props: ManageMappingModalProps) {
  const { data: formData } = useGetFormsQuery();
  const [postMapping] = usePostDataMappingMutation();
  const [putMapping] = usePutDataMappingMutation();
  const [delMapping] = useDeleteDataMappingMutation();
  const [messageApi, contextHolder] = message.useMessage();
  const [editedRecord, setEditedRecord] = useState(props.m ?? {
    ...newMappingTemplate
  });
  const [delConfirm, setDelConfirm] = useState("");
  const [isRedemptionEnabled, setIsRedemptionEnabled] = useState(
    props.m?.RedemptionDetails !== undefined)

  function handleUpdateRecord(key: string, value: any) {
    setEditedRecord((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  function handleUpdateItemDetailRecord(key: string, value: any) {
    setEditedRecord((prev) => ({
      ...prev,
      ItemDetails: {
        ...prev.ItemDetails,
          [key]: value,
      },
    }));
  }

  function handleUpdateRedemptionDetailRecord(key: string, value: any) {
    setEditedRecord(prev => ({
      ...prev,
      RedemptionDetails: {
        ...prev.RedemptionDetails,
        [key]: value
      }
    }));
  }

  /**
   * Handle image upload to s3 assets
   */
  function handleImageUpload() {
    message.info("Coming soon");
  }

  /**
   * Submit mapping
   */
  function handleSubmit() {
    if ((editedRecord.InternalSku ?? "").length === 0
        || (editedRecord.ItemDetails?.Title ?? "").length === 0
        || (editedRecord.VendorSku ?? "").length === 0
        || (editedRecord.VendorId ?? "").length === 0
        || (editedRecord.Type ?? "").length === 0) {
      messageApi.error("Base Mapping Data is required");
      return;
    }

    if (isRedemptionEnabled && (
        !editedRecord.FormId
        || editedRecord.RedemptionDetails === undefined
        || !editedRecord.RedemptionDetails.ImageUrl
        || !editedRecord.RedemptionDetails.Description
        || !editedRecord.RedemptionDetails.ApnSetting)) {
      messageApi.error("Redemption Data must be filled up");
      return;
    }

    if (props.m === null) {
      postMapping(editedRecord)
        .unwrap()
        .then(() => {
          message.success("Mapping record saved");
          props.onOk();
        })
        .catch(err => {
          messageApi.error(`Error saving mapping: ${err.data?.message ?? "Unknown error"}`);
        });
    } else {
      putMapping({
        id: props.m.Id,
        dataMapping: editedRecord
      })
        .unwrap()
        .then(() => {
          message.success("Mapping record updated");
          props.onOk();
        })
        .catch(err => {
          messageApi.error(`Error updating mapping: ${err.data?.message ?? "Unknown error"}`);
        });
    }
  }

  function handleDelete() {
    // Delete mapping without confirmation
    if (props.m === null) {
      return;
    }
    delMapping(props.m.Id)
      .unwrap()
      .then(() => {
        message.success("Deleted mapping "
                        + (props.m!!.ItemDetails?.Title ?? ""));
        props.onOk();
      })
      .catch(err => {
        messageApi.error("Error deleting mapping: "
                         + (props.m!!.ItemDetails?.Title ?? ""));
        console.error(err);
      });
  }

  function handleDeprecate() {
    const record = {...editedRecord};
    if (record.IsDeprecated) {
      record.IsDeprecated = false;
    } else {
      record.IsDeprecated = true;
    }
    console.log("Deprecating record", record);
    putMapping({
      id: props.m!.Id,
      dataMapping: record
    })
    .unwrap()
    .then(() => {
      message.success("Mapping record deprecated");
      setTimeout(() => window.location.reload(), 1000);
    })
    .catch(err => {
      messageApi.error(`Error deprecating mapping: ${err.data?.message ?? "Unknown error"}`);
    });
  }

  console.log("edited record", editedRecord);

  const BasicTab = <>
    {contextHolder}
    <Flex vertical gap={16}>
      <Text type="secondary">Base Mapping Data</Text>
      <Flex gap={16}>
        <Input
          addonBefore="Title"
          defaultValue=""
          value={editedRecord.ItemDetails?.Title}
          onChange={e => handleUpdateItemDetailRecord("Title", e.target.value)}
        />
        <Select
          value={editedRecord.Type}
          style={{ width: 200 }}
          options={productTypeOptions}
          onChange={e => handleUpdateRecord("Type", e)}
        />
      </Flex>
      <Flex gap={16}>
        <Tooltip title={"You are not encouraged to edit this if the product "
                        + "is in use!"}
        >
          <Input
            addonBefore="Internal Code"
            value={editedRecord.InternalSku}
            onChange={e => handleUpdateRecord("InternalSku", e.target.value)}
          />
        </Tooltip>
        <Input
          addonBefore="Provider Code"
          value={editedRecord.VendorSku}
          onChange={e => handleUpdateRecord("VendorSku", e.target.value)}
        />
      </Flex>
      <Tooltip title="Generally the first part of Internal Code, eg. joy, tgt">
        <Input
          addonBefore="Vendor ID"
          value={editedRecord.VendorId}
          onChange={e => handleUpdateRecord("VendorId", e.target.value)}
        />
      </Tooltip>
      <Tooltip title="Distributor Code">
        <Input
          addonBefore="SIMPLY Product Code"
          value={editedRecord.DistributorCode}
          onChange={e => handleUpdateRecord("DistributorCode", e.target.value)}
        />
      </Tooltip>
      <Tooltip title="TakeApp Variant ID">
        <Input
          addonBefore="TakeApp Variant ID"
          value={editedRecord.TakeappVariantId}
          onChange={e => handleUpdateRecord("TakeappVariantId", e.target.value)}
        />
      </Tooltip>
      <Text type="secondary">Country Data</Text>
      <CountryEditor
        countries={editedRecord.Countries ?? []}
        setCountries={countries => handleUpdateRecord("Countries", countries)}
      />
    </Flex>
  </>

  const formDropdown = formData?.map(x => (
    { label: x.Id, value: x.Id }
  )) ?? [];

  const RedemptionTab = <>
    <Flex vertical gap={16}>
      <Flex justify="space-between">
        <Text>Enabled?</Text>
        <Switch
          checked={isRedemptionEnabled}
          onChange={c => setIsRedemptionEnabled(c)}
        />
      </Flex>
      <Text type="secondary">Self-redemption variables</Text>
      <Flex justify="space-between">
        <Text>Form ID</Text>
        <Tooltip
          title="The redemption form required to be filled up by the user"
        >
          <Select
            options={formDropdown}
            value={editedRecord.FormId}
            onChange={e => handleUpdateRecord("FormId", e)}
            style={{ width: 300 }}
            disabled={!isRedemptionEnabled}
          />
        </Tooltip>
      </Flex>
      <Tooltip title="Same as Product Description on Basic tab">
        <Input
          readOnly
          addonBefore="Title"
          defaultValue=""
          value={editedRecord.ItemDetails?.Title}
          disabled={!isRedemptionEnabled}
        />
      </Tooltip>
      <Flex gap={16}>
        <Input
          addonBefore="Redemption Image"
          defaultValue=""
          value={editedRecord.RedemptionDetails?.ImageUrl}
          onChange={e =>
            handleUpdateRedemptionDetailRecord("ImageUrl", e.target.value)
          }
          disabled={!isRedemptionEnabled}
        />
        <UploadButton
          fileKey={"RedemptionDetail/ " + props.m?.Id}
          onSuccess={url => handleUpdateRedemptionDetailRecord("ImageUrl", url)}
        />
      </Flex>
      <Flex justify="center">
        <Image
          src={editedRecord.RedemptionDetails?.ImageUrl ?? "error"}
          fallback="data:image/png;base64,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"
          style={{ width: 552/2, height: 368/2, borderRadius: 16, objectFit: "cover" }}
        />
      </Flex>
      <Flex gap={4} vertical>
        <Text>Description</Text>
        <Input.TextArea
          value={editedRecord.RedemptionDetails?.Description}
          onChange={e =>
            handleUpdateRedemptionDetailRecord("Description", e.target.value)
          }
          disabled={!isRedemptionEnabled}
        />
      </Flex>
      <Text type="secondary">Installation Guide</Text>
      <Flex gap={4} vertical>
        <Text>APN Setting</Text>
        <Input.TextArea
          value={editedRecord.RedemptionDetails?.ApnSetting}
          onChange={e =>
            handleUpdateRedemptionDetailRecord("ApnSetting", e.target.value)
          }
          disabled={!isRedemptionEnabled}
        />
      </Flex>
    </Flex>
  </>

  const SimplyShopTab = <>
    <Flex vertical gap={16}>
      <Text type="secondary">Web Shop Management</Text>
      <Flex gap={16}>
        <Text>Enabled?</Text>
        <Switch
          checked={editedRecord.ItemDetails?.Enabled ?? false}
          onChange={e =>
            handleUpdateItemDetailRecord("Enabled", e)
          }
        />
      </Flex>
      <Tooltip title="Same as Product Description on Basic tab">
        <Input
          readOnly
          addonBefore="Title"
          defaultValue=""
          value={editedRecord.ItemDetails?.Title}
        />
      </Tooltip>
      <Flex gap={16}>
        <Input
          addonBefore="Shop Image"
          defaultValue=""
          value={editedRecord.ItemDetails?.ImageUrl}
          onChange={e =>
            handleUpdateItemDetailRecord("ImageUrl", e.target.value)
          }
        />
        <UploadButton
          fileKey={"WebShopForSku/" + props.m?.Id}
          onSuccess={url => handleUpdateItemDetailRecord("ImageUrl", url)}
        />
      </Flex>
      <Flex justify="center">
        <Image
          width={552/2}
          height={368/2}
          src={editedRecord.ItemDetails?.ImageUrl ?? "error"}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3PTWBSGcbGzM6GCKqlIBRV0dHRJFarQ0eUT8LH4BnRU0NHR0UEFVdIlFRV7TzRksomPY8uykTk/zewQfKw/9znv4yvJynLv4uLiV2dBoDiBf4qP3/ARuCRABEFAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghggQAQZQKAnYEaQBAQaASKIAQJEkAEEegJmBElAoBEgghgg0Aj8i0JO4OzsrPv69Wv+hi2qPHr0qNvf39+iI97soRIh4f3z58/u7du3SXX7Xt7Z2enevHmzfQe+oSN2apSAPj09TSrb+XKI/f379+08+A0cNRE2ANkupk+ACNPvkSPcAAEibACyXUyfABGm3yNHuAECRNgAZLuYPgEirKlHu7u7XdyytGwHAd8jjNyng4OD7vnz51dbPT8/7z58+NB9+/bt6jU/TI+AGWHEnrx48eJ/EsSmHzx40L18+fLyzxF3ZVMjEyDCiEDjMYZZS5wiPXnyZFbJaxMhQIQRGzHvWR7XCyOCXsOmiDAi1HmPMMQjDpbpEiDCiL358eNHurW/5SnWdIBbXiDCiA38/Pnzrce2YyZ4//59F3ePLNMl4PbpiL2J0L979+7yDtHDhw8vtzzvdGnEXdvUigSIsCLAWavHp/+qM0BcXMd/q25n1vF57TYBp0a3mUzilePj4+7k5KSLb6gt6ydAhPUzXnoPR0dHl79WGTNCfBnn1uvSCJdegQhLI1vvCk+fPu2ePXt2tZOYEV6/fn31dz+shwAR1sP1cqvLntbEN9MxA9xcYjsxS1jWR4AIa2Ibzx0tc44fYX/16lV6NDFLXH+YL32jwiACRBiEbf5KcXoTIsQSpzXx4N28Ja4BQoK7rgXiydbHjx/P25TaQAJEGAguWy0+2Q8PD6/Ki4R8EVl+bzBOnZY95fq9rj9zAkTI2SxdidBHqG9+skdw43borCXO/ZcJdraPWdv22uIEiLA4q7nvvCug8WTqzQveOH26fodo7g6uFe/a17W3+nFBAkRYENRdb1vkkz1CH9cPsVy/jrhr27PqMYvENYNlHAIesRiBYwRy0V+8iXP8+/fvX11Mr7L7ECueb/r48eMqm7FuI2BGWDEG8cm+7G3NEOfmdcTQw4h9/55lhm7DekRYKQPZF2ArbXTAyu4kDYB2YxUzwg0gi/41ztHnfQG26HbGel/crVrm7tNY+/1btkOEAZ2M05r4FB7r9GbAIdxaZYrHdOsgJ/wCEQY0J74TmOKnbxxT9n3FgGGWWsVdowHtjt9Nnvf7yQM2aZU/TIAIAxrw6dOnAWtZZcoEnBpNuTuObWMEiLAx1HY0ZQJEmHJ3HNvGCBBhY6jtaMoEiJB0Z29vL6ls58vxPcO8/zfrdo5qvKO+d3Fx8Wu8zf1dW4p/cPzLly/dtv9Ts/EbcvGAHhHyfBIhZ6NSiIBTo0LNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiECRCjUbEPNCRAhZ6NSiAARCjXbUHMCRMjZqBQiQIRCzTbUnAARcjYqhQgQoVCzDTUnQIScjUohAkQo1GxDzQkQIWejUogAEQo121BzAkTI2agUIkCEQs021JwAEXI2KoUIEKFQsw01J0CEnI1KIQJEKNRsQ80JECFno1KIABEKNdtQcwJEyNmoFCJAhELNNtScABFyNiqFCBChULMNNSdAhJyNSiEC/wGgKKC4YMA4TAAAAABJRU5ErkJggg=="
          style={{ borderRadius: 64, objectFit: "cover" }}
        />
      </Flex>
      <InputNumber
        addonBefore="Price"
        addonAfter="SGD"
        precision={2}
        value={editedRecord.ItemDetails?.Price}
        placeholder="No price set"
        onChange={e => handleUpdateItemDetailRecord("Price", e)}
      />
      <Flex gap={8} align="center">
        <Text>Local/SIMPLY</Text>
        <Select
          value={editedRecord.ItemDetails?.Type}
          style={{ minWidth: 150, flex: 1 }}
          options={webShopTypeOptions}
          onChange={e => handleUpdateItemDetailRecord("Type", e)}
        />
        <InputNumber
          addonBefore="Data"
          addonAfter="GB"
          precision={1}
          value={editedRecord.ItemDetails?.Gbs}
          placeholder="No GBs set"
          onChange={e => handleUpdateItemDetailRecord("Gbs", e)}
        />
        <InputNumber
          addonBefore="Days"
          precision={0}
          value={editedRecord.ItemDetails?.Days}
          placeholder="No days set"
          onChange={e => handleUpdateItemDetailRecord("Days", e)}
        />
        <Checkbox
          checked={editedRecord.ItemDetails?.IsSimplyForYou}
          onChange={e => handleUpdateItemDetailRecord("IsSimplyForYou",
                                                      e.target.checked)}
        >
          Simply for you
        </Checkbox>
      </Flex>
    </Flex>
  </>

  const DeletionTab = <>
    <Flex vertical gap={16}>
      <Text>Are you sure you wish to delete this mapping?</Text>
      <Text><strong>{props.m?.ItemDetails?.Title ?? "No title"}</strong></Text>
      <Text><strong>{props.m?.InternalSku}</strong></Text>
      <Text><strong>{props.m?.VendorSku}</strong></Text>
      <Text>
        This is not encouraged if the product has already been sold!
        This can cause loose links with existing order history etc.
        You are instead encouraged to mark this product as "Deprecated".
      </Text>
      <Tooltip title="This will remove the product from manual selection">
        <Button type={props.m?.IsDeprecated ? "default" : "primary"} onClick={handleDeprecate}>
          {props.m?.IsDeprecated ? "Unset Deprecated Status" : "Mark as Deprecated"}
        </Button>
      </Tooltip>
      <Text>
        To confirm, type into the text box below:&nbsp;
        <strong>Confirm</strong>
      </Text>
      <Flex gap={8}>
        <Input
          addonBefore="Confirmation"
          value={delConfirm}
          onChange={e => setDelConfirm(e.target.value)}
          style={{ flex: 1 }}
        />
        <Button
          danger
          type="primary"
          onClick={handleDelete}
          disabled={delConfirm.toLowerCase() !== "confirm"}
        >
          Delete
        </Button>
      </Flex>
    </Flex>
  </>

  return (
    <Modal
      title={`${props.m ? "Manage" : "Create"} Mapping Record`}
      width="80%"
      okText="Save"
      okButtonProps={{
        disabled: JSON.stringify(editedRecord) === JSON.stringify(props.m),
      }}
      {...props}
      onOk={handleSubmit}
    >
      <Tabs
        type="card"
        items={[
          {
            label: "Basic",
            key: "1",
            children: BasicTab
          },
          {
            label: "Redemption",
            key: "2",
            children: RedemptionTab
          },
          {
            label: "SIMPLY Web Shop",
            key: "3",
            children: SimplyShopTab
          },
          {
            label: "Delete",
            key: "999",
            children: DeletionTab,
            disabled: props.m === null
          }
        ]}
      />
    </Modal>
  );
}